import { BreakpointObserver } from "@angular/cdk/layout";
import { inject, Injectable, Type } from "@angular/core";
import { BehaviorSubject, combineLatest, startWith } from "rxjs";
import { environment } from "../../../../environments/environment";
import { AppConsts } from "@shared/AppConsts";
import { SCREEN_QUERY } from "@nx-workspace/fuse";
import { HealthEngageCommunicationService } from "@vhealth/engage/communication";
import { AppSessionService } from "@vadmin/account/auth";
import { SupportChatProvider } from "./support-chat-provider";
import { HubSpotChatProvider } from "./hubspot-chat.provider";
import { AsaflowChatProvider } from "./asaflow-chat.provider";

// Keyed by App:SupportChat, so switching vendors is a config change and a page reload, not a deploy (VS-1960).
const PROVIDERS = new Map<string, Type<SupportChatProvider>>([
    ["hubspot", HubSpotChatProvider],
    ["asaflow", AsaflowChatProvider],
]);

@Injectable({
    providedIn: "root",
})
export class SupportChatService {
    private _pageHides = new BehaviorSubject(0);
    private _provider?: SupportChatProvider;

    readonly enabled: boolean;

    constructor() {
        const providerType = PROVIDERS.get(AppConsts.SupportChat);
        this.enabled = environment.supportChatEnabled && !!providerType && !inject(AppSessionService).isImpersonating();
        if (!this.enabled) {
            return;
        }

        const provider = inject(providerType);
        provider.load();
        combineLatest([
            inject(BreakpointObserver).observe(SCREEN_QUERY.md),
            // A Subject only the Engage collect pages emit on: unseeded, combineLatest never fires (VS-1960).
            inject(HealthEngageCommunicationService).chatVisibility$.pipe(startWith(true)),
            this._pageHides,
        ]).subscribe(([screen, heChatVisible, pageHides]) => provider.setVisible(screen.matches && heChatVisible && pageHides === 0));
        this._provider = provider;
    }

    hide() {
        this._pageHides.next(this._pageHides.value + 1);
    }

    // Clamped: the xmpp sidebar can fold without having unfolded first.
    show() {
        this._pageHides.next(Math.max(0, this._pageHides.value - 1));
    }

    showMessengerPanel() {
        this._provider?.open();
    }
}
